import { useState, useEffect, useCallback } from 'react';
import { forecastService, formatForecastData } from '../../services/forecastService';
import { useWeatherLocation } from '../../contexts/WeatherLocationContext';
import { toForecastApiString } from '../../services/locationManager';

export default function useForecast() {
  const {
    activeLocation,
    loading: locationLoading,
    error: locationError,
    isDefault,
    isLive,
    activateCurrentLocation,
  } = useWeatherLocation();

  const [weatherData, setWeatherData] = useState(null);
  const [hourlyData, setHourlyData] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [selectedView, setSelectedView] = useState('nextDays');

  // Ephemeral preview: city searched on Forecast — never persisted.
  const [previewQuery, setPreviewQuery] = useState(null);

  const fetchWeatherData = useCallback(async (locationOrString, viewType = selectedView) => {
    try {
      setFetchLoading(true);
      setFetchError(null);

      const locationString =
        typeof locationOrString === 'string'
          ? locationOrString
          : toForecastApiString(locationOrString);

      let response;
      switch (viewType) {
        case 'nextDays':
          response = await forecastService.getNext7DaysForecast(locationString);
          break;
        case 'prevDays':
          response = await forecastService.getPrevious7DaysForecast(locationString);
          break;
        case 'nextHours':
          response = await forecastService.getHourlyForecast(locationString);
          break;
        case 'prevHours':
          response = await forecastService.getPreviousHourlyForecast(locationString);
          break;
        default:
          response = await forecastService.getNext7DaysForecast(locationString);
      }

      const formattedData = formatForecastData(response);
      setWeatherData({
        current: formattedData.daily[0] || {},
        location: formattedData.location,
      });
      setDailyData(formattedData.daily);
      setHourlyData(formattedData.hourly);
      return true;
    } catch (err) {
      console.error('Error fetching forecast data:', err);
      setFetchError(err.message || 'Failed to fetch weather data');
      return false;
    } finally {
      setFetchLoading(false);
    }
  }, [selectedView]);

  useEffect(() => {
    if (activeLocation && !previewQuery) {
      fetchWeatherData(activeLocation, selectedView);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLocation, selectedView]);

  useEffect(() => {
    if (previewQuery) {
      fetchWeatherData(`${previewQuery},`, selectedView);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewQuery, selectedView]);

  const searchLocation = useCallback((term) => {
    if (term?.trim()) setPreviewQuery(term.trim());
  }, []);

  const clearPreview = useCallback(() => setPreviewQuery(null), []);

  const getCurrentLocation = useCallback(async () => {
    setPreviewQuery(null);
    return activateCurrentLocation();
  }, [activateCurrentLocation]);

  const loading = locationLoading || fetchLoading;
  const error = fetchError || locationError;

  const location = previewQuery
    ? { name: previewQuery, city: previewQuery, lat: null, lng: null }
    : activeLocation;

  return {
    location,
    activeLocation,
    previewQuery,
    clearPreview,
    getCurrentLocation,
    getGeolocation: getCurrentLocation,
    searchLocation,
    weatherData,
    hourlyData,
    dailyData,
    loading,
    error,
    selectedView,
    setSelectedView,
    isCurrentLocation: isLive,
    isDefaultLocation: isDefault,
    refetchData: () =>
      fetchWeatherData(previewQuery ? `${previewQuery},` : activeLocation, selectedView),
  };
}
